import { PricingRangeBound } from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { roundRangeBoundValue } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filter-range-input';
import { SliderRange } from '@usepepper/sprout-kit-ui';

interface RangeBounds {
  max: number;
  min: number;
}

// Keeps the step count in 100–1000 whatever the span, so a drag feels the same
// on a $500 price column and a 2%-wide margin column.
const TARGET_STEP_COUNT = 100;
// Never finer than the two decimals the Min/Max fields display.
const MIN_STEP = 0.01;

export const getRangeSliderStep = ({ max, min }: RangeBounds): number => {
  const span = max - min;
  if (span <= 0) {
    return MIN_STEP;
  }

  return Math.max(
    10 ** Math.floor(Math.log10(span / TARGET_STEP_COUNT)),
    MIN_STEP,
  );
};

const clampToBounds = (value: number, { max, min }: RangeBounds): number =>
  Math.min(Math.max(value, min), max);

// An unset bound parks its thumb at that end of the track.
export const toSliderRange = (
  bound: PricingRangeBound,
  bounds: RangeBounds,
): SliderRange => {
  const start = clampToBounds(bound.min ?? bounds.min, bounds);
  const end = clampToBounds(bound.max ?? bounds.max, bounds);

  return start <= end ? [start, end] : [end, start];
};

// Inverse of `toSliderRange`. A thumb at the track's end reads as "no bound",
// so nudging one back to the edge doesn't leave the section active and
// dropping valueless rows.
export const fromSliderRange = (
  [start, end]: SliderRange,
  bounds: RangeBounds,
): PricingRangeBound => ({
  max: end >= bounds.max ? null : roundRangeBoundValue(end),
  min: start <= bounds.min ? null : roundRangeBoundValue(start),
});
